import { computed, reactive, ref, unref, watch } from '@vue/composition-api'
import * as Sentry from '@sentry/browser'
import { Referral } from '@/api/model/referral'
import { Company } from '@/api/model/company'
import { Pipeline } from '@/api/model/pipeline'
import { useModal } from '@/composables/useModal'
import { useSteps } from '@/composables/useSteps'
import { v4 as uuid } from 'uuid'
import {
    AttachmentAPI,
    CommentAPI,
    CompanyAPI,
    ContactAPI,
    MentionAPI,
    PipelineAPI,
    ProjectAPI,
    ReferralAPI,
    SourceAPI,
} from '@/api/backend-client'
import { Contact } from '@/api/model/contact'
import { User } from '@/api/model/user'
import { Project } from '@/api/model/project'
import { ReferralNode } from '@/api/model/referral-node'
import { PipelineConnection } from '@/api/model/pipeline-connection'
import {
    BranchType,
    CommentOrigin,
    CommentVisibility,
    ReferralChannel,
    ReferralNodeType,
    ReferralStatus,
} from '@/api/types'

import { Branch } from '@/api/model/branch'
import { Source } from '@/api/model/source'
import { useCurrentData } from '@/composables/useCurrentData'
import { getAllowedUsers } from '@/helpers/security'
import { Comment } from '@/api/model/comment'
import { datetime } from '@/plugins/filters-date'
import i18n from '@/i18n'
import router from '@/router'
// vue-router 3.x exposes this as a static on the class, not as a named export.
import VueRouter from 'vue-router'
import Vue from 'vue'
import { escapeHtml } from '@/helpers/html'

const modal = reactive(useModal())
const steps = reactive(useSteps({ initialStep: 1, maxSteps: 4 }))
const { currentUser, currentCompany } = useCurrentData()

const projects = reactive<Project[]>([])
const outgoingNodes = reactive<ReferralNode[]>([])
const outgoingConnections = reactive<PipelineConnection[]>([])
const ownPipelines = reactive<Pipeline[]>([])

const isVisible = computed(() => modal.isVisible)
const usePipelines = computed(() => state.targetCompany && state.targetCompany.pipelinesEnabled)


const state = reactive({
    loading: ref<boolean>(true),
    submitting: ref<boolean>(false),
    targetCompany: ref<Company>(null),
    targetPipeline: ref<Pipeline>(),
    comment: ref<string>(null),
    commentVisibility: ref<CommentVisibility>(null),
    updateExistingContact: ref<boolean>(false),

    gdpr: ref<string>(null),
    gdprFile: ref<File>(null),

    followUp: reactive<{ type: string, informUsers: User[] }>({
        type: null,
        informUsers: [],
    }),

    referral: reactive(Referral.fromData({
        contact: Contact.fromData({

        }),
    })),
})

const referringToOwnCompany = computed(() => {
    if (!currentCompany.value) {
        return false
    }

    if (!state.referral.assignedToBranch) {
        return false
    }

    return currentCompany.value.branches
        .some(branch => branch.id === state.referral.assignedToBranch.id)
})

const availablePipelines = computed<Pipeline[]>(() => {
    if (!currentCompany.value) {
        return []
    }

    if (!state.targetCompany) {
        return []
    }

    // If referring to self, show all pipelines.
    if (state.targetCompany.id === currentCompany.value.id) {
        return (ownPipelines as Pipeline[])
            .filter(pipeline => pipeline.active)
    }

    // Build list of active pipelines.
    return outgoingConnections
        .filter((connection: PipelineConnection) => connection.sourceEnabled)
        .filter((connection: PipelineConnection) => connection.targetEnabled)
        .filter((connection: PipelineConnection) => connection.pipeline !== null)
        .filter((connection: PipelineConnection) => connection.targetCompany.id === state.targetCompany.id)
        .map((connection: PipelineConnection) => connection.pipeline)
        .filter(pipeline => pipeline.active)

        // Fix cyclic error, we keep targetCompany seperate anyway.
        .map((pipeline: Pipeline) => ({ ...pipeline, company: null } as Pipeline))
})

const availableReferralNodeTypes = computed<ReferralNodeType[]>(() => {
    if (!state.targetCompany) {
        return []
    }

    return outgoingNodes
        .filter((node: ReferralNode) => node.active)
        .filter((node: ReferralNode) => node.target.id === state.targetCompany.id)
        .map((node: ReferralNode) => node.type)
})


const availableCompanies = reactive<Company[]>([])
function updateAvailableCompanies(): void {
    const companies = new Map<string, Company>()

    outgoingNodes
        .filter((node: ReferralNode) => node.active)
        .forEach((node: ReferralNode) => companies.set(node.target.id, node.target))

    outgoingConnections
        .filter((connection: PipelineConnection) => connection.sourceEnabled)
        .filter((connection: PipelineConnection) => connection.targetEnabled)
        .filter((connection: PipelineConnection) => connection.pipeline !== null)
        .forEach((connection: PipelineConnection) => companies.set(connection.targetCompany.id, connection.targetCompany))

    availableCompanies.splice(0)
    availableCompanies.push(...Array.from(companies.values()))

    availableCompanies.sort((a, b) => a.name.localeCompare(b.name))

    // Insert current company if not already added
    if (currentCompany.value && !availableCompanies.some(company => company.id === currentCompany.value.id)) {
        availableCompanies.unshift(currentCompany.value)
    }
}

const availableSources = ref<Source[]>([])
let availableSourcesUpdate = 0
async function updateAvailableSources(): Promise<void> {
    // Only the latest update fills the list: one for an earlier company that answers later must not
    const update = ++availableSourcesUpdate
    availableSources.value.splice(0)
    if (!state.targetCompany) {
        return
    }

    let sources: Source[]|undefined
    try {
        sources = await SourceAPI.getAll({
            company: state.targetCompany.id,
            channel: ReferralChannel.USER,
            active: 1,
        })

    } catch (error) {
        sources = undefined
    }

    // The client gives undefined for a failed request (or throws)
    if (!sources) {
        if (update === availableSourcesUpdate) {
            Vue.notify({
                type: 'error',
                title: 'Foutmelding',
                text: 'De bronnen konden niet geladen worden. Kies het kantoor opnieuw of probeer het later nogmaals.',
            })
        }

        return
    }

    if (update !== availableSourcesUpdate) {
        return
    }

    availableSources.value.push(...sources)
}

const referringUsers = reactive<User[]>([])
function updateReferringUsers(): void {
    if (!currentUser.value || !currentCompany.value) {
        return
    }

    // If we're not referring from a branch yet, only allow picking own user.
    if (!state.referral.referredByBranch) {
        referringUsers.splice(0)
        referringUsers.push(currentUser.value)
        return
    }

    // Store previously selected user.
    const selectedUser = state.referral.referredBy

    // Reactive way to clear and fill array with available users.
    referringUsers.splice(0)
    referringUsers.push(...currentCompany.value.getAvailableUsers({
        branch: state.referral.referredByBranch as Branch,
    }))

    // Unset selected user if it does not exist in the new list of available users.
    if (selectedUser && referringUsers.some(user => user.id === selectedUser.id) === false) {
        state.referral.referredBy = null
    }
}

const referringBranches = reactive<Branch[]>([])
function updateReferringBranches(): void {
    if (!currentUser.value) {
        return
    }

    // Reactive way to clear and fill array.
    referringBranches.splice(0)
    referringBranches.push(...currentUser.value.branches
        .filter(branch => branch.type === BranchType.DEFAULT)
        .sort((a, b) => a.name.localeCompare(b.name)))
}

const assignableUsers = reactive<User[]>([])
function updateAssignableUsers(): void {
    if (!state.targetCompany) {
        assignableUsers.splice(0)
        return
    }

    if (!state.referral.assignedToBranch) {
        assignableUsers.splice(0)
        return
    }

    assignableUsers.splice(0)
    assignableUsers.push(...state.targetCompany.getAvailableUsers({
        branch: state.referral.assignedToBranch as Branch,
    }))

    // An assigned user of the previously chosen branch is no longer on offer
    if (state.referral.assignedTo && !assignableUsers.some(user => user.id === state.referral.assignedTo.id)) {
        state.referral.assignedTo = null
    }
}

const assignableBranches = reactive<Branch[]>([])
async function updateAssignableBranches(): Promise<void> {
    if (!state.targetCompany) {
        assignableBranches.splice(0)
        return
    }

    // Clear existing branches.
    assignableBranches.splice(0)

    // Push legacy branches if pipelines is disabled.
    if (state.targetCompany.pipelinesEnabled === false) {
        assignableBranches.push(...state.targetCompany.getAvailableBranches()
            .filter(branch => branch.acceptsReferralType(state.referral.type)))
        assignableBranches.sort((a, b) => a.name.localeCompare(b.name))
        forgetBranchIfNotOffered()

        return
    }

    // Check if targetPipeline is set.
    if (!state.targetPipeline) {
        return
    }

    // Push available branches from target pipeline.
    assignableBranches.push(...state.targetPipeline.branches

        // targetPipeline.branches has no company set, so we need to set it manually.
        // this is used for security later to determine which users are available.
        .map(branch => Branch.fromData({ ...branch, company: state.targetCompany })))
    assignableBranches.sort((a, b) => a.name.localeCompare(b.name))
    forgetBranchIfNotOffered()
}

/**
 * A branch chosen from an earlier list (another referral type, or the previous pipeline that was still shown while
 * this one loaded) goes, with what was chosen for it
 */
function forgetBranchIfNotOffered(): void {
    const branch = state.referral.assignedToBranch
    if (branch && !assignableBranches.some(offered => offered.id === branch.id)) {
        forgetBranchChoices()
    }
}

const informableUsers = reactive<User[]>([])
function updateInformableUsers(): void {
    if (!state.targetCompany) {
        informableUsers.splice(0)
        return
    }

    if (!state.referral.assignedToBranch) {
        informableUsers.splice(0)
        return
    }

    informableUsers.splice(0)
    informableUsers.push(...state.targetCompany.getAvailableUsers()
        .filter(user => {

            // Can always inform a company manager.
            if (user.isCompanyManager) {
                return true
            }

            // If user is part of this branch, they can be informed.
            if ((user.branches || []).some(branch => branch.id === state.referral.assignedToBranch.id)) {

                // Only if they are branch manager or team lead.
                return user.isBranchManager || user.isTeamLead
            }

            return false
        }))

    // Users chosen to inform for the previously chosen branch go when they are no longer on offer
    const informable = new Set(informableUsers.map(user => user.id))
    for (let index = state.followUp.informUsers.length - 1; index >= 0; index--) {
        if (!informable.has(state.followUp.informUsers[index].id)) {
            state.followUp.informUsers.splice(index, 1)
        }
    }
}

const mentionableUsers = reactive<User[]>([])
let mentionableUsersUpdate = 0
async function updateMentionableUsers(): Promise<void> {
    // Only the latest update fills the list: an earlier one that answers later would add its users again, or the
    // users of a branch that is no longer chosen
    const update = ++mentionableUsersUpdate
    mentionableUsers.splice(0)

    const users = await getAllowedUsers(
        state.referral.referredByBranch as Branch,
        unref(currentUser),
        state.referral.assignedToBranch as Branch,
        state.referral.assignedTo as User,
    )

    if (update === mentionableUsersUpdate) {
        mentionableUsers.splice(0, mentionableUsers.length, ...users)
    }
}

function mentionedUsers(comment: string): User[] {
    return mentionableUsers
        .filter(user => comment.indexOf(`data-id="${user.id}"`) >= 0) as User[]
}

watch(() => [
    state.targetCompany,
], updateAvailableSources)

watch(() => [
    currentUser.value,
    currentCompany.value,
    state.referral.referredByBranch,
], updateReferringUsers)

watch(() => [
    currentUser.value,
    state.referral.type,
    usePipelines.value,
], updateReferringBranches)

watch(() => [
    state.targetCompany,
    state.referral.assignedToBranch,
], updateAssignableUsers)

watch(() => [
    state.targetCompany,
    state.targetPipeline,
    state.referral.type,
], updateAssignableBranches)

watch(() => [
    state.targetCompany,
    state.referral.assignedToBranch,
], updateInformableUsers)

watch(() => [
    state.referral.referredByBranch,
    state.referral.assignedToBranch,
    state.referral.assignedTo,
], updateMentionableUsers)

// Update state when inform type changes.
watch(() => state.followUp.type, () => {

    // Clear informable users when followup type if not inform.
    if (state.followUp.type !== 'inform') {
        state.followUp.informUsers.splice(0)
    }

    // Clear assignedTo if followup type is not assign.
    if (state.followUp.type !== 'assign') {
        state.referral.assignedTo = null
    }
})

// Auto select first company if there is only one option: through setTargetCompany, as a choice of the user's, so it
// is the full company (a referral node only has the partner card) and an earlier choice doesn't overwrite it
watch(availableCompanies, () => {
    if (availableCompanies.length === 1 && state.targetCompany?.id !== availableCompanies[0].id) {
        setTargetCompany(availableCompanies[0].id)
    }
})

// Auto select first referrable user if it's the only option.
watch(referringUsers, () => {
    if (referringUsers.length === 1) {
        state.referral.referredBy = referringUsers[0]
    }
})

// Auto select first referrable branch if it's the only option.
watch(referringBranches, () => {
    if (referringBranches.length === 1) {
        state.referral.referredByBranch = referringBranches[0]
    }
})

// Auto select first assignable branch if it's the only option.
watch(assignableBranches, () => {
    if (assignableBranches.length === 1) {
        state.referral.assignedToBranch = assignableBranches[0]
    }
})


/**
 * Load data for lead form.
 * (splice/push is reactive, rather than overwriting the array)
 */
async function loadData(): Promise<void> {
    state.loading = true

    projects.splice(0)
    projects.push(...await ProjectAPI.getAll({ company: currentCompany.value.id }))

    outgoingNodes.splice(0)
    outgoingNodes.push(...await CompanyAPI.getOutgoingReferrals())

    outgoingConnections.splice(0)
    outgoingConnections.push(...await CompanyAPI.getOutgoingConnections())

    updateAvailableCompanies()

    ownPipelines.splice(0)
    ownPipelines.push(...await PipelineAPI.getAll({ company: currentCompany.value.id }))

    state.loading = false
}

// The latest choice of company and of pipeline. A response for an earlier choice, also one from a previous time the
// form was open, must not overwrite it (reset() counts as a choice too).
let targetCompanyChoice = 0
let targetCompanyLoaded = 0
/** The company of the latest choice (targetCompanyChoice) */
let targetCompanyRequested: string|null = null
let targetPipelineChoice = 0

/**
 * Choose the company to refer to. Its pipeline, referral type, source, assigned branch and user, and the users to inform belong
 * to the previous company, so they are reset, also once the company has loaded: meanwhile they could be chosen from the
 * previous company's options.
 */
async function setTargetCompany(id: string): Promise<void> {
    // The company that is chosen already (loaded, or still loading) is no new choice: the select sends it again when
    // step 1 mounts (Previous) or when it gets its value, and that must not reset what was chosen for it
    const loaded = targetCompanyLoaded === targetCompanyChoice
    if ((loaded && state.targetCompany && state.targetCompany.id === id) || (!loaded && targetCompanyRequested === id)) {
        return
    }

    const choice = ++targetCompanyChoice
    targetCompanyRequested = id
    forgetCompanyChoices()

    // Fetched fresh, because we need *ALL* context: an office linked since has its users and branches only now
    const company = await CompanyAPI.get(id, true)
    if (choice !== targetCompanyChoice) {
        return
    }

    if (!company) {
        Vue.notify({
            type: 'error',
            title: 'Foutmelding',
            text: 'Het kantoor kon niet geladen worden. Kies het opnieuw of probeer het later nogmaals.',
        })
    }

    forgetCompanyChoices()
    state.targetCompany = company || null
    targetCompanyLoaded = choice
}

function forgetCompanyChoices(): void {
    targetPipelineChoice++
    state.targetPipeline = null
    state.referral.type = null
    state.referral.newSource = null
    forgetBranchChoices()
}

/**
 * What was chosen from the branches of a company or pipeline: the assigned branch and user, the follow-up and the
 * users to inform (an "assign" follow-up without its user would fail after the lead was created)
 */
function forgetBranchChoices(): void {
    state.referral.assignedToBranch = null
    state.referral.assignedTo = null
    state.followUp.type = null
    state.followUp.informUsers.splice(0)
}

/**
 * Choose the pipeline; fetched because we need to know its branches. Ignored while another company is loading, as the
 * pipelines shown then are still those of the previous company.
 */
async function setTargetPipeline(id: string|null): Promise<void> {
    if (targetCompanyLoaded !== targetCompanyChoice) {
        return
    }

    // The assigned branch (and so its user and the users to inform) came from the previous pipeline's branches
    if ((state.targetPipeline?.id || null) !== id) {
        forgetBranchChoices()
    }

    const companyChoice = targetCompanyChoice
    const choice = ++targetPipelineChoice
    let pipeline: Pipeline|null|undefined = null
    try {
        pipeline = id ? await PipelineAPI.get(id) : null

    } catch (error) {
        pipeline = undefined
    }

    // The client gives undefined for a failed request (or throws)
    if (pipeline === undefined) {
        if (choice === targetPipelineChoice) {
            Vue.notify({
                type: 'error',
                title: 'Foutmelding',
                text: 'De pipeline kon niet geladen worden. Kies hem opnieuw of probeer het later nogmaals.',
            })
        }

        return
    }

    if (choice === targetPipelineChoice && companyChoice === targetCompanyChoice) {
        state.targetPipeline = pipeline
    }
}

/**
 * Called when opening lead form.
 */
async function open(): Promise<void> {
    reset()
    modal.open()
    await loadData()
}

/**
 * Called when closing lead form.
 */
function close(): void {
    modal.close()
}

/**
 * Reset form to initial state.
 */
function reset(): void {
    steps.reset()

    // A company or pipeline still loading from before doesn't apply to the new form
    targetCompanyLoaded = ++targetCompanyChoice
    targetPipelineChoice++

    state.referral = reactive(Referral.fromData({
        id: uuid(),
        contact: Contact.fromData({
            id: uuid(),
            gender: 'unknown',
        }),
        referredBy: currentUser.value,
        referredByBranch: currentUser.value?.branches.length === 1 ? currentUser.value.branches[0] : null,
        score: 0,
        newSource: null,
        status: 'new',
    }))

    state.followUp = reactive<{ type: string, informUsers: User[] }>({
        type: null,
        informUsers: [],
    })

    state.comment = null
    state.commentVisibility = currentCompany.value?.defaultCommentVisibility || null
    state.updateExistingContact = false
    state.targetCompany = null
    state.targetPipeline = null
    state.gdpr = null
    state.gdprFile = null
}

/**
 * Called when submitting lead form.
 */
async function submit(): Promise<void> {
    state.submitting = true

    // Set once the lead exists server-side. Everything after creation (notifications,
    // comments, GDPR attachment, mentions, navigation) is a follow-up step: if one of those
    // fails the lead is still created, so the user must not be told it failed - that is what
    // makes them submit again and produces duplicate leads.
    let createdReferralId: string|null = null

    try {
        // If we selected an existing contact, update that seperately.
        if (state.updateExistingContact) {
            await ContactAPI.update(
                state.referral.contact.id,
                state.referral.contact,
            )

        } else {
            await ContactAPI.create({
                ...state.referral.contact,
                company: currentCompany.value.resourceIdentifier as unknown as Company,
                newSource: state.referral.newSource.resourceIdentifier as unknown as Source,
            } as Contact)
        }

        // Manage referral values when pipelines are used.
        if (usePipelines.value) {
            state.referral.status = ReferralStatus.OPEN
            state.referral.pipeline = state.targetPipeline
            // @todo: fix wrong state being assigned by ReferralBuilder -> backend

        // @todo: remove this when pipelines are used everywhere.
        } else {
            state.referral.status = ReferralStatus.NEW
            state.referral.pipeline = null
        }

        // Create new referral.
        const newReferral = await ReferralAPI.create({
            ...state.referral,
            contact: state.referral.contact?.resourceIdentifier || null,
            pipeline: state.referral.pipeline?.resourceIdentifier || null,
            referredBy: state.referral.referredBy?.resourceIdentifier || null,
            referredByBranch: state.referral.referredByBranch?.resourceIdentifier || null,
            assignedTo: state.referral.assignedTo?.resourceIdentifier || null,
            assignedToBranch: state.referral.assignedToBranch?.resourceIdentifier || null,
            newSource: state.referral.newSource?.resourceIdentifier || null,
        } as unknown as Referral)

        // The API can return no referral even though the lead was created server-side (the insert
        // succeeds and the request fails afterwards, e.g. on a 500). Dereferencing newReferral below
        // would throw a cryptic "Cannot read properties of undefined (reading 'id'/'assignedToBranch')".
        // Fail with an explicit message instead, and do NOT tell the user the lead was not created -
        // retrying is what produces duplicate leads.
        if (!newReferral || !newReferral.id) {
            throw new Error(
                'De lead is mogelijk wel aangemaakt, maar er ging iets mis bij het afronden. '
                + 'Ververs de pagina en controleer of de lead er al staat voordat je het opnieuw probeert.',
            )
        }

        createdReferralId = newReferral.id

        // Helper to create comment.
        const createComment = async (referral: Referral, comment: string, user: User|null = null, origin: CommentOrigin = null, visibility: CommentVisibility = CommentVisibility.PUBLIC): Promise<Comment> => {
            return await CommentAPI.create(Comment.fromData({
                referral: referral.resourceIdentifier,
                user: user ? user.resourceIdentifier : null,
                id: uuid(),
                comment,
                origin,
                visibility,
            }))
        }

        // Helper to create GDPR comment.
        const createGdprComment = async (referral: Referral, commentTranslationKey: string) => {
            await createComment(
                newReferral,
                i18n.t(commentTranslationKey, {
                    timestamp: datetime(new Date()),
                    userName: currentUser.value.name,
                    assignedToBranchName: newReferral.assignedToBranch.name,
                    assignedToCompanyName: newReferral.assignedToBranch.company.name,
                }).toString(),
            )
        }

        // When followup type is assign, we inform assigned user if assigned user is someone else
        if (state.followUp.type === 'assign' && state.referral.assignedTo
            && state.referral.assignedTo.id !== currentUser.value.id) {
            await ReferralAPI.notify(
                newReferral.id,
                'assignedUser',
                [],
            )
        }

        // When followup type is inform, we inform all selected users.
        if (state.followUp.type === 'inform') {
            const targetIds = state.followUp.informUsers.map(user => user.id)
            await ReferralAPI.notify(
                newReferral.id,
                'branchManagers', // the API expects 'branchManagers' but any user can be a target
                targetIds,
            )
        }

        // When creating a lead for someone else, we place a comment stating this.
        if (state.referral.referredBy.id !== currentUser.value.id) {
            await createComment(
                newReferral,
                i18n.t('forms.create-task-form.referringUserDifferentComment', {
                    referredByUserName: newReferral.referredBy.name,
                    userName: currentUser.value.name,
                }).toString(),
            )
        }

        // Place additional comment when source is sitekick.
        if (state.referral.newSource?.name?.toLowerCase() === 'sitekick') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.verbal-comment-sitekick',
            )

        // We place an additional comment when GDPR approval is verbal.
        } else if (state.gdpr === 'verbal') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.verbal-comment',
            )

        // For written approval we store the attached file.
        } else if (state.gdpr === 'written') {
            await createGdprComment(
                newReferral,
                'forms.create-task-form.gdpr.written-comment',
            )

            await AttachmentAPI.createWithFile(
                newReferral,
                uuid(),
                state.gdprFile,
            )
        }

        // Create entered comment.
        if (state.comment?.length > 0) {
            const comment = await createComment(newReferral, state.comment, newReferral.referredBy, CommentOrigin.USER, state.commentVisibility)

            const commentMentionedUsers = mentionedUsers(comment.comment)
            if (commentMentionedUsers.length > 0) {
                await MentionAPI.mention(comment, commentMentionedUsers)
            }
        }

        // Refresh referral
        await ReferralAPI.get(newReferral.id, true)

        // Close modal.
        close()

        // Mark referral created so we can show this on the detail page.
        window.keyvue.referralCreated = true

        // Push detail route.
        //
        // A router guard deliberately aborts this navigation for Beta users: router/index.ts
        // redirects them to the One app via window.location and returns next(false). vue-router
        // rejects the push in that case, which is NOT a lead-creation failure - it used to end up
        // in the catch below and showed the user an error on a perfectly successful create.
        await router.push({
            name: 'tasks_detail',
            params: {
                referralId: newReferral.id,
                contactId: newReferral.contact.id,
                toOne: this.$currentUser.betaOptInEnabled,
            },
        }).catch((navigationError) => {
            if (!VueRouter.isNavigationFailure(navigationError)) {
                throw navigationError
            }
        })

    } catch (error) {
        // Report to Sentry as well as showing the toast. Without this the failure is only ever
        // visible to the user in a notification, which is why lead-creation failures went
        // undiagnosed: nothing reached our error tracking.
        Sentry.captureException(error, {
            tags: {
                feature: 'create-lead',
                phase: createdReferralId ? 'post-create' : 'create',
            },
            extra: {
                createdReferralId,
                targetCompanyId: state.targetCompany?.id,
                targetPipelineId: state.targetPipeline?.id,
                followUpType: state.followUp?.type,
            },
        })

        // The lead was created and a follow-up step failed: say so explicitly, otherwise the
        // user submits again and ends up with duplicate leads.
        if (createdReferralId) {
            // Close the modal: the lead exists, so leaving the filled-in form open with an
            // active submit button invites exactly the re-submit we are warning against.
            close()

            Vue.notify({
                type: 'warn',
                title: 'Let op',
                text: 'De lead is aangemaakt, maar een vervolgstap (bijvoorbeeld een notificatie of notitie) '
                    + 'is misgegaan. Ververs de pagina - de lead staat er al. Maak hem niet opnieuw aan.',
            })

            return
        }

        Vue.notify({
            type: 'error',
            title: 'Foutmelding',
            text: escapeHtml(error.message),
        })

    } finally {
        state.submitting = false
    }
}

export function useCreateLeadForm() {
    return {
        state,
        steps,

        open,
        close,
        submit,
        setTargetCompany,
        setTargetPipeline,

        isVisible,
        usePipelines,
        referringToOwnCompany,
        availableCompanies,
        availableSources,
        availablePipelines,
        availableReferralNodeTypes,

        referringUsers,
        assignableUsers,
        referringBranches,
        assignableBranches,
        informableUsers,
        mentionableUsers,

        projects,
    }
}
